import React, { useEffect } from 'react';
import { Image, Skeleton, Spin } from 'antd';
import LazyLoad, {
  forceCheck,
  forceVisible,
  LazyLoadProps
} from 'react-lazyload';
import { useOne } from 'app/definitions/hooks/data/useOne';
import { IQMASDocument } from 'app/definitions/interfaces/Document';
import { useIntersectionObserver } from 'app/modules/ui/hooks/useIntersectionObserver';
import classes from './DocumentItem.module.scss';

export type QMASLazyDocumentProps = {
  className?: string;
  alt?: string;
  width?: number;
  height?: number;
  skeleton?: boolean;
  placeholder?: React.ReactNode;
  documentId: string | number;
  resource?: string;
  forceVisible?: boolean;
  forceCheck?: boolean;
  content?: React.ReactNode;
  lazyProps?: Partial<LazyLoadProps>;
};

const Content = (props: QMASLazyDocumentProps) => {
  const { alt, documentId, skeleton, width, height = 65 } = props;
  const { data, isLoading, isFetching } = useOne({
    resource: 'document-list',
    id: documentId
  });

  const { mineType, url: src } = (data?.data ?? {}) as IQMASDocument;

  const renderLoading = () => {
    if (skeleton) {
      return (
        <Skeleton
          active
          avatar={{ shape: 'square', size: 'large' }}
          paragraph={false}
          title={false}
          style={{ height, width: height }}
        ></Skeleton>
      );
    }
    return <Spin size="small" />;
  };

  const renderContent = () => {
    switch (mineType) {
      case 'image/jpeg':
      case 'image/png':
      case 'image/gif':
      case 'image/webp':
        return (
          <Image
            src={src}
            alt={alt}
            width={width}
            height={height}
            preview={false}
          />
        );
      case 'video/mp4':
      case 'video/webm':
        return <video src={src} width={width} height={height} controls />;
      case 'application/pdf':
        return (
          <embed
            src={src}
            width={width}
            height={height}
            type="application/pdf"
          />
        );
      default:
        return <div>Unsupported MIME type</div>;
    }
  };

  return (
    <a
      className={classes.documentItem}
      style={{
        borderRadius: '4px',
        display: 'inline-block'
      }}
      href={src || '#'}
      target="_blank"
      rel="noreferrer"
    >
      {isLoading || isFetching ? renderLoading() : renderContent()}
    </a>
  );
};

export const QMASLazyDocument = (props: QMASLazyDocumentProps) => {
  const {
    height = 65,
    placeholder,
    forceVisible: forceVisibleFromProps,
    forceCheck: forceCheckFromProps,
    lazyProps,
    content = undefined
  } = props;

  const [ref, entry] = useIntersectionObserver();

  if (entry?.isIntersecting) {
    setTimeout(() => {
      forceVisible();
    }, 250);
  }

  useEffect(() => {
    if (forceVisibleFromProps) {
      setTimeout(() => {
        forceVisible();
      }, 500);
    }
  }, [forceVisibleFromProps]);

  useEffect(() => {
    if (forceCheckFromProps) {
      setTimeout(() => {
        forceCheck();
      }, 250);
    }
  }, [forceCheckFromProps]);

  return (
    <div ref={ref}>
      <LazyLoad
        height={height}
        placeholder={
          typeof placeholder !== 'undefined' ? (
            placeholder
          ) : (
            <Skeleton
              active
              avatar={{ shape: 'square', size: 'large' }}
              paragraph={false}
              title={false}
            ></Skeleton>
          )
        }
        offset={50}
        {...lazyProps}
      >
        {typeof content === 'undefined' ? <Content {...props} /> : content}
      </LazyLoad>
    </div>
  );
};
